<template>
  <card no-body body-classes="px-0 pb-1" footer-classes="pb-2">
    <template slot="header">
      <div class="d-flex justify-content-between">
        <h3 class="mb-0 mt-2">
          <span v-if="filters.status !== 'pending'">{{ year }} </span>
          <span v-else>All </span>
          <span v-if="filters.status">{{ filters.status | capitalize }} </span>
          Transactions
        </h3>
        <userpilot-flow :id="userpilotId" :variant="'outline-primary'"></userpilot-flow>
      </div>
    </template>
    <div>
      <b-collapse visible id="filters">
        <b-row>
          <b-col cols="12" class="d-flex justify-content-center justify-content-sm-between flex-wrap mt-4 px-5"
          >
            <el-select
              class="select-primary pagination-select"
              v-model="filters.status"
              placeholder="All Statuses">
              <el-option
                class="select-primary"
                label="All Statuses"
                value=""
              ></el-option>
              <el-option
                class="select-primary"
                v-for="status in statuses"
                :key="status.value"
                :label="status.label"
                :value="status.value"
              >
              </el-option>
            </el-select>

            <transaction-views :fields="tableColumns"></transaction-views>

            <el-select v-model="year" v-if="filters.status !== 'pending'">
              <el-option v-for="year in years"
                :key="year.label"
                :label="year.label"
                :value="year.value">
              </el-option>
            </el-select>

            <el-select
              class="select-primary pagination-select"
              v-model="perPage"
              placeholder="Per page"
            >
              <el-option
                class="select-primary"
                v-for="item in pagination.perPageOptions"
                :key="item"
                :label="item"
                :value="item"
              >
              </el-option>
            </el-select>
          </b-col>
        </b-row>
        <b-row>
          <b-col class="my-4 px-5">
            <b-input-group class="mt-3">
              <template #prepend>
                <b-input-group-text><i class="fas fa-search"></i></b-input-group-text>
              </template>
              <b-form-input v-model="searchQuery" placeholder="Search..."></b-form-input>
              <template #append>
                <b-button size="sm" variant="success" @click="$emit('getItems', year)">Search</b-button>
                <b-button size="sm" @click="resetFilters">Reset Filters</b-button>
                <b-button size="sm" variant="danger" @click="$emit('exportTransactions')">Export Transactions</b-button>
              </template>
            </b-input-group>
          </b-col>
        </b-row>
      </b-collapse>
      <b-table sticky-header="800px" responsive small no-sort-reset :items="tableData" :busy="isBusy" :fields="visibleColumns" :perPage="pagination.perPage" :no-local-sorting="true" :sort-by.sync="sortBy" :sort-desc.sync="sortDir" @sort-changed="sortingChanged" :tbody-tr-class="rowClass">
        <template #table-busy>
          <loading-panel></loading-panel>
        </template>

        <template #cell(status)="row">
          <b-badge :variant="variants[row.item.status]">{{ row.item.status }}</b-badge>
        </template>

        <template #cell(units)="row">
          <span v-if="row.item.units == true">Yes</span>
          <span v-else>No</span>
        </template>

        <template #cell(contingent_sale)="row">
          <span v-if="row.item.contingent_sale == true">Yes</span>
          <span v-else>No</span>
        </template>

        <template #cell(full_address)="row">
          {{ row.item.property.full_address }}
        </template>

        <template #cell(client_name)="row">
          {{ row.item.client.name }}
        </template>

        <template #cell(client_email)="row">
          {{ row.item.client.email }}
        </template>

        <template #cell(client_phone)="row">
          {{ row.item.client.phone }}
        </template>

        <template #cell(target_mls_date)="row">
          <span v-if="row.item.status == 'coming-soon'">
            {{ row.item.target_mls_date }}
          </span>
        </template>

        <template #cell(active_date)="row">
          <span v-if="row.item.status == 'active'">
            {{ row.item.active_date }}
          </span>
        </template>

        <template #cell(projected_close)="row">
          <span v-if="row.item.status == 'pending'">
            {{ row.item.projected_close }}
          </span>
        </template>

        <template #cell(close_date)="row">
          <span v-if="row.item.status == 'closed'">
            {{ row.item.close_date }}
          </span>
        </template>

        <template #cell(expiration_date)="row">
          {{ row.item.expiration_date }}
        </template>

        <template #cell(days_on_market)="row">
          <span v-if="row.item.status == 'active'">{{ row.item.days_on_market }}</span>
        </template>

        <template #cell(sale_price)="row">
          {{ row.item.sale_price | currency }}
        </template>

        <template #cell(listing_price)="row">
          {{ row.item.listing_price | currency }}
        </template>

        <template #cell(transaction_fee)="row">
          {{ row.item.transaction_fee | currency }}
        </template>

        <template #cell(amount)="row">
          <span v-if="row.item.commission_type == 0">{{ row.item.amount }}%</span>
          <span v-else>${{ row.item.amount }}</span>
        </template>

        <template #cell(gross_commission)="row">
          {{ row.item.gross_commission | currency }}
        </template>

        <template #cell(net_commission)="row">
          {{ row.item.net_commission | currency }}
        </template>

        <template #cell(other_cos)="row">
          {{ row.item.other_cos | currency }}
        </template>

        <template #cell(cos_details)="row">
          <b-button size="sm" variant="primary" @click="showCosModal(row)">
            Show Details
          </b-button>
        </template>

        <template #cell(gross_profit)="row">
          {{ row.item.gross_profit | currency }}
        </template>

        <template #cell(actions)="row">
          <b-dropdown text="Actions" size="sm" variant="info" boundary="viewport">
            <b-dropdown-item variant="primary" @click="handleEdit(row)" v-if="canAddEdit"><i class="fa fa-edit"></i> Edit</b-dropdown-item>
            <b-dropdown-item variant="danger" @click="handleDelete(row)" v-if="canAddEdit"><i class="fa fa-trash"></i> Delete</b-dropdown-item>
            <b-dropdown-item variant="info" @click="notesModal(row)"><i class="fa fa-edit"></i> Notes</b-dropdown-item>
            <b-dropdown-item variant="info" v-b-toggle.deal-sidebar @click="dealId = row.item.deal_id" v-if="row.item.deal_id"><img src="/img/fub/fub-icon.png" class="mr-3" /> FUB Deal</b-dropdown-item>
          </b-dropdown>
        </template>

        <template #cell(primary_agent)="row">
          <div class="d-flex align-items-center">
            <span class="avatar avatar-sm rounded-circle mr-3" v-if="row.item.primary_agent">
              <img :alt="row.item.primary_agent + ' avatar'" :src="'https://vapor-us-east-1-1603342423.s3.amazonaws.com/' + row.item.agent_image">
            </span>
            <span v-if="row.item.primary_agent">{{ row.item.primary_agent }}</span>
          </div>
        </template>

        <template #cell(members)="row">
          <b-button size="sm" variant="primary" @click="showCosModal(row)">
            COS Details
          </b-button>
        </template>

        <template #cell(primary_source)="row">
          <span v-if="row.item.primary_source">{{ row.item.primary_source }}</span>
          <span v-if="row.item.primary_source && row.item.source"> - </span>
          <span v-if="row.item.source">{{ row.item.source }}</span>
        </template>
      </b-table>
    </div>
    <div
      slot="footer"
      class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap"
    >
      <div class="">
        <p class="card-category">
          Showing {{ pagination.from }} to {{ pagination.to }} of {{ pagination.total }} entries

          <span v-if="selectedRows.length">
            &nbsp; &nbsp; {{selectedRows.length}} rows selected
          </span>
        </p>

      </div>
      <base-pagination
        class="pagination-no-border"
        :current="pagination.currentPage"
        :per-page="pagination.perPage"
        :total="pagination.total"
        @change="paginationChanged($event)"
      >
      </base-pagination>
    </div>
    <notes-modal :type="'transactions'"
                 :typeId="typeId"
                 :userRoute="userRoute">
    </notes-modal>
    <cost-of-sale-modal :transaction="transaction"></cost-of-sale-modal>
    <deal :dealId="dealId" :userRoute="userRoute"></deal>
  </card>
</template>
<script>
import axios from 'axios';
import swal from 'sweetalert2';
import { Select, Option } from 'element-ui';
import { BasePagination } from '@/components';
import NotesModal from '@/views/Components/Modals/NotesModal';
import CostOfSaleModal from '@/views/Components/Modals/CostOfSaleModal';
import TransactionViews from './Views/TransactionViews';
import LoadingPanel from '@/components/LoadingPanel';
import UserpilotFlow from '@/views/Components/UserpilotFlow';
import Deal from '@/views/Components/FUB/Deal';
import { dateFilter } from 'vue-date-fns';

export default {
  filters: {
    date: dateFilter
  },
  components: {
    BasePagination,
    [Select.name]: Select,
    [Option.name]: Option,
    NotesModal,
    CostOfSaleModal,
    TransactionViews,
    LoadingPanel,
    UserpilotFlow,
    Deal
  },
  props: {
    data: [],
    tableColumns: [],
    propsToSearch: null,
    statuses: null,
    variants: null,
    canAddEdit: null,
    userRoute: null,
    orderBy: null,
    orderDir: null,
    isBusy: null,
    pagination: null,
    userpilotId: null
  },
  data() {
    return {
      tableData: [],
      selectedRows: [],
      filters: {
        status: '',
      },
      year: new Date().getFullYear(),
      years: [
        { value: new Date().getFullYear() - 6, label: new Date().getFullYear() - 6 },
        { value: new Date().getFullYear() - 5, label: new Date().getFullYear() - 5 },
        { value: new Date().getFullYear() - 4, label: new Date().getFullYear() - 4 },
        { value: new Date().getFullYear() - 3, label: new Date().getFullYear() - 3 },
        { value: new Date().getFullYear() - 2, label: new Date().getFullYear() - 2 },
        { value: new Date().getFullYear() - 1, label: new Date().getFullYear() - 1 },
        { value: new Date().getFullYear(), label: new Date().getFullYear() },
        { value: new Date().getFullYear() + 1, label: new Date().getFullYear() + 1 },
        { value: new Date().getFullYear() + 2, label: new Date().getFullYear() + 2 },
        { value: new Date().getFullYear() + 3, label: new Date().getFullYear() + 3 },
      ],
      typeId: null,
      sortBy: this.orderBy,
      sortDir: this.orderDir,
      transaction: null,
      searchQuery: '',
      perPage: this.pagination.perPage,
      dealId: null
    };
  },
  computed: {
    visibleColumns() {
      return this.tableColumns.filter(field => field.visible)
    },
    admin() {
      return this.$store.getters['roles/admin'];
    }
  },
  methods: {
    paginationChanged(page) {
      this.$emit('pageChange', page);
    },
    handleLike(index, row) {
      swal.fire({
        title: `You liked ${row.name}`,
        buttonsStyling: false,
        type: 'success',
        confirmButtonClass: 'btn btn-success btn-fill'
      });
    },
    handleEdit(row) {
      const path = this.$route.fullPath + '/' + row.item.id;
      this.$router.push({
        path: path,
      });
    },
    handleDelete(row) {
      swal.fire({
        title: 'Are you sure?',
        text: `You won't be able to revert this!`,
        type: 'warning',
        showCancelButton: true,
        confirmButtonClass: 'btn btn-success btn-fill',
        cancelButtonClass: 'btn btn-danger btn-fill',
        confirmButtonText: 'Yes, delete it!',
        buttonsStyling: false
      }).then(result => {
        if (result.value) {
          this.deleteRow(row);
          swal.fire({
            title: 'Deleted!',
            text: `Your transaction has been deleted.`,
            type: 'success',
            showConfirmButton: false,
            timer: 1500,
            icon: 'success'
          });
        }
      });
    },
    deleteRow(row) {
      axios.delete(this.userRoute + "/transactions/" + row.item.id)
      .then(response => {
        this.tableData.splice(row.index, 1);
      })
      .catch(error => {
        this.error(error);
      });
    },
    selectionChange(selectedRows) {
      this.selectedRows = selectedRows
    },
    notesModal(row) {
      this.typeId = row.item.id;
      this.$bvModal.show('notes-modal');
    },
    checkRouteQuery() {
      if(this.$route.query.status) {
        this.filters.status = this.$route.query.status;
      }

      if(this.$route.query.year) {
        this.year = this.$route.query.year;
      }
    },
    showCosModal(row) {
      this.transaction = row.item;
      this.$bvModal.show('cos-modal');
    },
    clearTransaction() {
      this.transaction = null;
    },
    sortingChanged(ctx) {
      this.$emit('sort', ctx);
    },
    resetFilters() {
      this.year = new Date().getFullYear();
      this.filters.status = '';
      this.searchQuery = '';
      this.perPage = 25;
      this.$emit('getItems', this.year)
    },
    rowClass(item, type) {
      if (!item || type !== 'row') return
      if (item.incomplete) return 'table-danger'
    }
  },
  created() {
    this.checkRouteQuery();
    this.tableData = this.data;
  },
  watch: {
    year(to, from) {
      this.$emit('updateYear', to);
      this.$emit('getItems', this.year);
    },
    searchQuery(to, from) {
      this.$emit('search', to);
    },
    perPage(to, from) {
      this.$emit('perPageChange', to);
      this.$emit('getItems', this.year);
    },
    'filters.status'(to, from) {
      this.$emit('updateStatus', to);
      this.$emit('getItems', this.year);
    },
    'pagination.currentPage'(to, from) {
      this.$emit('getItems', this.year);
    },
    sortBy(to, from) {
      this.$emit('getItems', this.year);
    },
    sortDir(to, from) {
      this.$emit('getItems', this.year);
    },
    data(to, from) {
      this.tableData = to;
    }
  }
}
</script>
<style>
  .no-border-card .card-footer{
    border-top: 0;
  }
</style>